/**
 * Clock
 *
 * Compact single-line clock in the app header: the localised date beside the
 * running 24h time. The time is a figure, so it is set in JetBrains Mono
 * (app-shell.md §3); the date rides alongside it in secondary ink.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const localeMap = { ru: 'ru-RU', en: 'en-US' };

const UZ_MONTHS_LONG = [
  'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
  'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
];

const Clock = () => {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const lang = i18n.language || 'uz';
  const locale = localeMap[lang] || 'en-US';

  const formattedDate = useMemo(() => {
    if (lang === 'uz') {
      return `${now.getDate()} ${UZ_MONTHS_LONG[now.getMonth()]}`;
    }
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(now);
  }, [now, locale, lang]);

  const formattedTime = useMemo(() => {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(now);
  }, [now, locale]);

  return (
    <div className="lms-clock" aria-label={t('common.serverTime')}>
      <span className="lms-clock__date">{formattedDate}</span>
      <span className="lms-clock__time">{formattedTime}</span>
    </div>
  );
};

export default Clock;
